import { useState } from "react";
import {
  Trash2,
  Plus,
  GripVertical,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
  BookOpen,
  ShieldAlert,
  Swords,
  HeartHandshake,
  Users as UsersIcon,
  ServerCog,
  AlertTriangle,
  Crown,
  Sparkles,
  Shield,
  Zap,
  Map as MapIcon,
  Lock,
} from "lucide-react";
import {
  EditorShell,
  Field,
  TextInput,
  TextArea,
  useSectionState,
} from "@/admin/EditorShell";
import ImageUpload from "@/admin/ImageUpload";

const setField = (obj, key, val) => ({ ...obj, [key]: val });

// ============ HERO ============
export const HeroEditor = () => {
  const [v, setV] = useSectionState("hero");
  if (!v) return null;
  return (
    <EditorShell
      title="Hero — Sezione iniziale"
      description="Il primo blocco che vedono i visitatori: badge, titolo a due righe e sottotitolo."
      section="hero"
      value={v}
      setValue={setV}
    >
      <Field label="Badge (pillola in alto)">
        <TextInput value={v.badge} onChange={(x) => setV(setField(v, "badge", x))} />
      </Field>
      <Field label="Titolo riga 1">
        <TextInput value={v.tagline_top} onChange={(x) => setV(setField(v, "tagline_top", x))} />
      </Field>
      <Field label="Titolo riga 2">
        <TextInput value={v.tagline_bottom} onChange={(x) => setV(setField(v, "tagline_bottom", x))} />
      </Field>
      <Field label="Sottotitolo">
        <TextArea value={v.subtitle} onChange={(x) => setV(setField(v, "subtitle", x))} rows={3} />
      </Field>
      <Field label="Testo CTA principale">
        <TextInput value={v.cta_primary_label} onChange={(x) => setV(setField(v, "cta_primary_label", x))} />
      </Field>
      <Field label="Testo CTA secondaria">
        <TextInput value={v.cta_secondary_label} onChange={(x) => setV(setField(v, "cta_secondary_label", x))} />
      </Field>
    </EditorShell>
  );
};

// ============ LORE ============
const LORE_CHAPTER_ICONS = [
  "Building2",
  "Shield",
  "Users2",
  "Crown",
  "Sparkles",
  "Map",
  "Zap",
  "AlertTriangle",
  "BookOpen",
  "ServerCog",
];

const LoreChapterCard = ({ c, idx, total, onChange, onRemove, onMove }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-4 rounded-2xl border border-[#D4AF37]/20 bg-[#0B0B0F]/70 overflow-hidden">
      <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-[#1A1A22]/70 to-transparent">
        <button
          onClick={() => setOpen((o) => !o)}
          className="p-1.5 rounded text-[#B6B6BE] hover:text-[#F5F5F5]"
          aria-label={open ? "Comprimi" : "Espandi"}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <span className="font-display text-3xl text-[#D4AF37] glow-gold w-12 text-center shrink-0">
          {c.n || String(idx + 1)}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-mono-pl text-[9px] uppercase tracking-[0.22em] text-[#D4AF37]">
            Capitolo {c.n || String(idx + 1)} · {c.icon}
          </p>
          <p className="font-display text-base uppercase text-[#F5F5F5] tracking-wide truncate">
            {c.title || "(senza titolo)"}
          </p>
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={() => onMove(idx, -1)}
            disabled={idx === 0}
            className="p-2 rounded border border-white/10 text-[#B6B6BE] disabled:opacity-30"
          >
            <ArrowUp size={12} />
          </button>
          <button
            onClick={() => onMove(idx, 1)}
            disabled={idx === total - 1}
            className="p-2 rounded border border-white/10 text-[#B6B6BE] disabled:opacity-30"
          >
            <ArrowDown size={12} />
          </button>
          <button
            onClick={onRemove}
            className="p-2 rounded border border-[#E26B6B]/35 text-[#E26B6B]"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {open && (
        <div className="p-5 sm:p-6 border-t border-white/[0.04]">
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Numero romano">
              <TextInput
                value={c.n}
                onChange={(x) => onChange({ ...c, n: x.toUpperCase() })}
                placeholder="I, II, III…"
              />
            </Field>
            <Field label="Icona">
              <select
                value={c.icon || "Building2"}
                onChange={(e) => onChange({ ...c, icon: e.target.value })}
                className="search-input w-full px-3 py-3 rounded-xl bg-[#0B0B0F]/90 border border-white/10 text-sm text-[#F5F5F5] focus:outline-none focus:border-[#D4AF37]/55"
              >
                {LORE_CHAPTER_ICONS.map((ic) => (
                  <option key={ic} value={ic}>
                    {ic}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Titolo capitolo">
              <TextInput value={c.title} onChange={(x) => onChange({ ...c, title: x })} />
            </Field>
          </div>
          <Field label="Sottotitolo (corsivo dorato sotto il titolo)">
            <TextInput
              value={c.subtitle}
              onChange={(x) => onChange({ ...c, subtitle: x })}
              placeholder="es. I Colletti Bianchi"
            />
          </Field>
          <Field label="Corpo narrativo (usa righe vuote per separare i paragrafi)">
            <TextArea
              value={c.body}
              onChange={(x) => onChange({ ...c, body: x })}
              rows={10}
            />
          </Field>
        </div>
      )}
    </div>
  );
};

export const LoreEditor = () => {
  const [v, setV] = useSectionState("lore");
  if (!v) return null;

  const chapters = Array.isArray(v.chapters) ? v.chapters : [];

  const updateChapter = (i, next) =>
    setV({ ...v, chapters: chapters.map((c, j) => (j === i ? next : c)) });
  const removeChapter = (i) =>
    setV({ ...v, chapters: chapters.filter((_, j) => j !== i) });
  const moveChapter = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= chapters.length) return;
    const arr = [...chapters];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setV({ ...v, chapters: arr });
  };
  const addChapter = () => {
    const next = chapters.length + 1;
    const romans = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
    setV({
      ...v,
      chapters: [
        ...chapters,
        {
          n: romans[next - 1] || String(next),
          icon: "Sparkles",
          title: "Nuovo capitolo",
          body: "Testo narrativo…",
        },
      ],
    });
  };

  return (
    <EditorShell
      title="Lore — Il volume narrativo"
      description="Gestisci il blocco 'L'Illusione d'Oro': titolo cinematografico, paragrafi introduttivi, timeline verticale dei capitoli ed epigrafe finale. Tutto editabile in-place, nessun codice."
      section="lore"
      value={v}
      setValue={setV}
    >
      {/* HERO INTRO */}
      <div className="mb-8">
        <p className="font-mono-pl text-[10px] uppercase tracking-[0.22em] text-[#00D4FF] mb-3">
          Apertura del volume
        </p>
        <Field label="Kicker (badge piccolo in alto)">
          <TextInput value={v.kicker} onChange={(x) => setV({ ...v, kicker: x })} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Titolo — prima parte">
            <TextInput
              value={v.title_lead}
              onChange={(x) => setV({ ...v, title_lead: x })}
              placeholder="es. L'Illusione"
            />
          </Field>
          <Field label="Titolo — parte accent (oro)">
            <TextInput
              value={v.title_accent}
              onChange={(x) => setV({ ...v, title_accent: x })}
              placeholder="es. d'Oro"
            />
          </Field>
        </div>
        <Field label="Paragrafo introduttivo">
          <TextArea
            value={v.intro_p1}
            onChange={(x) => setV({ ...v, intro_p1: x })}
            rows={5}
          />
        </Field>
        <Field label="Tagline (paragrafo finale in maiuscolo)">
          <TextArea
            value={v.intro_p2}
            onChange={(x) => setV({ ...v, intro_p2: x })}
            rows={2}
          />
        </Field>
      </div>

      {/* CHAPTERS TIMELINE */}
      <div className="mb-8 pt-6 border-t border-white/[0.06]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-mono-pl text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
              Capitoli della timeline
            </p>
            <p className="text-xs text-[#B6B6BE]/70 mt-1">
              Ogni capitolo appare nella timeline verticale dorata sul sito.
            </p>
          </div>
          <button
            onClick={addChapter}
            data-testid="add-lore-chapter"
            className="btn-ghost-neon inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.16em]"
          >
            <Plus size={12} /> Aggiungi capitolo
          </button>
        </div>

        {chapters.length === 0 && (
          <p className="text-sm text-[#B6B6BE]/60 italic py-4 text-center">
            Nessun capitolo. Aggiungine almeno uno per popolare la timeline.
          </p>
        )}

        {chapters.map((c, i) => (
          <LoreChapterCard
            key={i}
            c={c}
            idx={i}
            total={chapters.length}
            onChange={(next) => updateChapter(i, next)}
            onRemove={() => removeChapter(i)}
            onMove={moveChapter}
          />
        ))}
      </div>

      {/* OUTRO */}
      <div className="pt-6 border-t border-white/[0.06]">
        <p className="font-mono-pl text-[10px] uppercase tracking-[0.22em] text-[#A78BFA] mb-3">
          Epigrafe finale
        </p>
        <Field label="Kicker outro">
          <TextInput
            value={v.outro_kicker}
            onChange={(x) => setV({ ...v, outro_kicker: x })}
            placeholder="es. Fine del Volume"
          />
        </Field>
        <Field label="Citazione finale">
          <TextArea
            value={v.outro_body}
            onChange={(x) => setV({ ...v, outro_body: x })}
            rows={3}
          />
        </Field>
      </div>
    </EditorShell>
  );
};

// ============ LINKS ============
export const LinksEditor = () => {
  const [v, setV] = useSectionState("links");
  if (!v) return null;
  return (
    <EditorShell
      title="Link & Social"
      description="URL ufficiali — Discord, Cfx, candidatura, social."
      section="links"
      value={v}
      setValue={setV}
    >
      <Field label="Discord (invito server)">
        <TextInput value={v.discord} onChange={(x) => setV(setField(v, "discord", x))} placeholder="https://discord.gg/..." />
      </Field>
      <Field label="Cfx Join (entrata server FiveM)">
        <TextInput value={v.join} onChange={(x) => setV(setField(v, "join", x))} placeholder="https://cfx.re/join/..." />
      </Field>
      <Field label="Compila il BG (canale Discord candidature)">
        <TextInput value={v.whitelist} onChange={(x) => setV(setField(v, "whitelist", x))} placeholder="https://discord.com/channels/..." />
      </Field>
      <Field label="Twitch (opzionale)">
        <TextInput value={v.twitch} onChange={(x) => setV(setField(v, "twitch", x))} />
      </Field>
      <Field label="YouTube (opzionale)">
        <TextInput value={v.youtube} onChange={(x) => setV(setField(v, "youtube", x))} />
      </Field>
      <Field label="Instagram (opzionale)">
        <TextInput value={v.instagram} onChange={(x) => setV(setField(v, "instagram", x))} />
      </Field>
      <Field label="TikTok (opzionale)">
        <TextInput value={v.tiktok} onChange={(x) => setV(setField(v, "tiktok", x))} />
      </Field>
    </EditorShell>
  );
};

// ============ CHARACTER ============
export const CharacterEditor = () => {
  const [v, setV] = useSectionState("character");
  if (!v) return null;
  return (
    <EditorShell
      title="Character Showcase"
      description="Il blocco 'Whitelist obbligatoria' con il personaggio."
      section="character"
      value={v}
      setValue={setV}
    >
      <ImageUpload
        value={v.image_url}
        onChange={(url) => setV(setField(v, "image_url", url))}
        label="Immagine del personaggio (PNG con sfondo trasparente consigliato)"
        height={260}
      />
      <Field label="Kicker (badge)">
        <TextInput value={v.kicker} onChange={(x) => setV(setField(v, "kicker", x))} />
      </Field>
      <Field label="Titolo — parte iniziale">
        <TextInput value={v.title_lead} onChange={(x) => setV(setField(v, "title_lead", x))} />
      </Field>
      <Field label="Titolo — parte oro (accent)">
        <TextInput value={v.title_accent} onChange={(x) => setV(setField(v, "title_accent", x))} />
      </Field>
      <Field label="Descrizione">
        <TextArea value={v.description} onChange={(x) => setV(setField(v, "description", x))} rows={4} />
      </Field>
      <Field label="Testo bottone CTA">
        <TextInput value={v.cta_label} onChange={(x) => setV(setField(v, "cta_label", x))} />
      </Field>
    </EditorShell>
  );
};

// ============ FACTIONS ============
const FACTION_COLORS = ["#00D4FF", "#FFB100", "#E26B6B", "#D4AF37", "#A78BFA", "#34D399"];

export const FactionsEditor = () => {
  const [v, setV] = useSectionState("factions");
  if (!v) return null;

  const update = (i, key, val) =>
    setV(v.map((f, idx) => (idx === i ? { ...f, [key]: val } : f)));

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= v.length) return;
    const arr = [...v];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    arr.forEach((f, k) => (f.code = String(k + 1).padStart(2, "0")));
    setV(arr);
  };

  const remove = (i) => setV(v.filter((_, idx) => idx !== i));

  const add = () =>
    setV([
      ...v,
      {
        key: `faction-${Date.now()}`,
        code: String(v.length + 1).padStart(2, "0"),
        name: "Nuova fazione",
        desc: "Descrizione…",
        color: FACTION_COLORS[v.length % FACTION_COLORS.length],
      },
    ]);

  return (
    <EditorShell
      title="Forze dell'Ordine — Fazioni"
      description="Aggiungi, modifica o riordina le fazioni mostrate sulla home."
      section="factions"
      value={v}
      setValue={setV}
    >
      {v.map((f, i) => (
        <div
          key={f.key || i}
          className="mb-5 rounded-2xl p-5 border border-white/8 bg-[#0B0B0F]/50"
          data-testid={`faction-card-${i}`}
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="font-mono-pl text-[10px] tracking-[0.22em] text-[#D4AF37]">
              {f.code} · {f.name}
            </span>
            <div className="ml-auto flex gap-1">
              <button onClick={() => move(i, -1)} className="p-1.5 rounded border border-white/10 text-[#B6B6BE] hover:text-[#F5F5F5]" aria-label="Sposta su">
                <ArrowUp size={12} />
              </button>
              <button onClick={() => move(i, 1)} className="p-1.5 rounded border border-white/10 text-[#B6B6BE] hover:text-[#F5F5F5]" aria-label="Sposta giù">
                <ArrowDown size={12} />
              </button>
              <button onClick={() => remove(i)} className="p-1.5 rounded border border-[#E26B6B]/35 text-[#E26B6B]" aria-label="Elimina">
                <Trash2 size={12} />
              </button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome">
              <TextInput value={f.name} onChange={(x) => update(i, "name", x)} />
            </Field>
            <Field label="Colore (hex)">
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={f.color || "#D4AF37"}
                  onChange={(e) => update(i, "color", e.target.value)}
                  className="w-12 h-10 rounded border border-white/10 bg-transparent cursor-pointer"
                />
                <TextInput value={f.color} onChange={(x) => update(i, "color", x)} />
              </div>
            </Field>
          </div>
          <Field label="Sottotitolo / Ruolo" hint='Es. "Los Santos Police Department". Se vuoto viene mostrato il codice.'>
            <TextInput
              value={f.role || ""}
              onChange={(x) => update(i, "role", x)}
              placeholder="Los Santos Police Department"
            />
          </Field>
          <Field label="Descrizione">
            <TextArea value={f.desc} onChange={(x) => update(i, "desc", x)} rows={2} />
          </Field>
          <Field label="Logo / Immagine (URL o path)" hint="Es. https://... oppure /uploads/xxx.png. Se vuoto viene mostrata l'icona di default.">
            <TextInput
              value={f.image_url || ""}
              onChange={(x) => update(i, "image_url", x)}
              placeholder="https://..."
            />
          </Field>
        </div>
      ))}
      <button
        onClick={add}
        data-testid="add-faction"
        className="mt-2 btn-ghost-neon inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.18em]"
      >
        <Plus size={13} /> Aggiungi fazione
      </button>
    </EditorShell>
  );
};

// ============ FEATURES ============
const FEATURE_ICONS = ["Sparkles", "Shield", "Users", "Crown", "Zap", "Map", "Star", "Lock", "Trophy", "Globe"];

export const FeaturesEditor = () => {
  const [v, setV] = useSectionState("features");
  if (!v) return null;

  const update = (i, key, val) =>
    setV(v.map((f, idx) => (idx === i ? { ...f, [key]: val } : f)));
  const remove = (i) => setV(v.filter((_, idx) => idx !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= v.length) return;
    const arr = [...v];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setV(arr);
  };
  const add = () =>
    setV([...v, { icon: "Sparkles", title: "Nuova feature", desc: "Descrizione…" }]);

  return (
    <EditorShell
      title="Features — Punti chiave"
      description="I bullet point con icone mostrati nella sezione Features."
      section="features"
      value={v}
      setValue={setV}
    >
      {v.map((f, i) => (
        <div key={i} className="mb-5 rounded-2xl p-5 border border-white/8 bg-[#0B0B0F]/50">
          <div className="flex justify-end gap-1 mb-2">
            <button onClick={() => move(i, -1)} className="p-1.5 rounded border border-white/10 text-[#B6B6BE]"><ArrowUp size={12} /></button>
            <button onClick={() => move(i, 1)} className="p-1.5 rounded border border-white/10 text-[#B6B6BE]"><ArrowDown size={12} /></button>
            <button onClick={() => remove(i)} className="p-1.5 rounded border border-[#E26B6B]/35 text-[#E26B6B]"><Trash2 size={12} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Icona (Lucide)">
              <select
                value={f.icon}
                onChange={(e) => update(i, "icon", e.target.value)}
                className="search-input w-full px-3 py-3 rounded-xl bg-[#0B0B0F]/90 border border-white/10 text-sm text-[#F5F5F5] focus:outline-none focus:border-[#D4AF37]/55"
              >
                {FEATURE_ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
              </select>
            </Field>
            <Field label="Titolo">
              <TextInput value={f.title} onChange={(x) => update(i, "title", x)} />
            </Field>
          </div>
          <Field label="Descrizione">
            <TextInput value={f.desc} onChange={(x) => update(i, "desc", x)} />
          </Field>
        </div>
      ))}
      <button
        onClick={add}
        data-testid="add-feature"
        className="mt-2 btn-ghost-neon inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.18em]"
      >
        <Plus size={13} /> Aggiungi feature
      </button>
    </EditorShell>
  );
};

// ============ GALLERY ============
export const GalleryEditor = () => {
  const [v, setV] = useSectionState("gallery");
  if (!v) return null;

  const update = (i, key, val) =>
    setV(v.map((g, idx) => (idx === i ? { ...g, [key]: val } : g)));
  const remove = (i) => setV(v.filter((_, idx) => idx !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= v.length) return;
    const arr = [...v];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setV(arr);
  };
  const add = () =>
    setV([
      ...v,
      { key: `gallery-${Date.now()}`, src: "", caption: "Nuova immagine", tag: "Tag" },
    ]);

  return (
    <EditorShell
      title="Galleria — Immagini"
      description="Carica nuove foto o sostituiscile. Trascina &amp; rilascia o clicca per uploadare."
      section="gallery"
      value={v}
      setValue={setV}
    >
      {v.map((g, i) => (
        <div key={g.key || i} className="mb-7 rounded-2xl p-5 border border-white/8 bg-[#0B0B0F]/50">
          <div className="flex justify-between items-center mb-3">
            <p className="font-mono-pl text-[10px] tracking-[0.22em] text-[#D4AF37]">
              {String(i + 1).padStart(2, "0")} · {g.caption || "Senza titolo"}
            </p>
            <div className="flex gap-1">
              <button onClick={() => move(i, -1)} className="p-1.5 rounded border border-white/10 text-[#B6B6BE]"><ArrowUp size={12} /></button>
              <button onClick={() => move(i, 1)} className="p-1.5 rounded border border-white/10 text-[#B6B6BE]"><ArrowDown size={12} /></button>
              <button onClick={() => remove(i)} className="p-1.5 rounded border border-[#E26B6B]/35 text-[#E26B6B]"><Trash2 size={12} /></button>
            </div>
          </div>
          <ImageUpload
            value={g.src}
            onChange={(url) => update(i, "src", url)}
            label="Immagine"
            height={180}
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Caption">
              <TextInput value={g.caption} onChange={(x) => update(i, "caption", x)} />
            </Field>
            <Field label="Tag (es. Law, Garage, Veicoli, Lifestyle)">
              <TextInput value={g.tag} onChange={(x) => update(i, "tag", x)} />
            </Field>
          </div>
        </div>
      ))}
      <button
        onClick={add}
        data-testid="add-gallery"
        className="mt-2 btn-ghost-neon inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.18em]"
      >
        <Plus size={13} /> Aggiungi immagine
      </button>
    </EditorShell>
  );
};

// ============ RULES (Regolamento avanzato) ============
const CHAPTER_ICONS = [
  "BookOpen",
  "HeartHandshake",
  "Swords",
  "ShieldAlert",
  "AlertTriangle",
  "Users",
  "ServerCog",
  "Crown",
  "Sparkles",
  "Shield",
  "Zap",
  "Map",
];

const ICON_MAP = {
  BookOpen,
  HeartHandshake,
  Swords,
  ShieldAlert,
  AlertTriangle,
  Users: UsersIcon,
  ServerCog,
  Crown,
  Sparkles,
  Shield,
  Zap,
  Map: MapIcon,
};

const sectionType = (s) => {
  if (s.robberies) return "robberies";
  if (s.table) return "table";
  if (s.defs) return "defs";
  if (s.list) return "list";
  return "body";
};

const SECTION_TEMPLATES = {
  body: { h: "Nuova sezione", body: "Testo descrittivo…" },
  list: { h: "Nuova lista", list: ["Punto 1", "Punto 2"] },
  defs: { h: "Definizioni", defs: [{ t: "Termine", d: "Definizione…" }] },
  table: {
    h: "Nuova tabella",
    table: {
      head: ["Colonna 1", "Colonna 2", "Colonna 3"],
      rows: [
        ["a", "b", "c"],
        ["d", "e", "f"],
      ],
    },
  },
  robberies: {
    h: "Regole rapine",
    robberies: [
      {
        name: "Nuova rapina",
        sub: "Descrizione breve",
        stats: { rapinatori: "1 — 4", poliziotti: "2 — 4", ostaggi: "—" },
        rules: ["Regola 1"],
      },
    ],
  },
};

const SECTION_LABELS = {
  body: "Paragrafo",
  list: "Elenco puntato",
  defs: "Definizioni (cards)",
  table: "Tabella",
  robberies: "Card rapine",
};

const SECTION_BADGE_COLOR = {
  body: "text-[#B6B6BE] border-white/15",
  list: "text-[#00D4FF] border-[#00D4FF]/40",
  defs: "text-[#A78BFA] border-[#A78BFA]/40",
  table: "text-[#D4AF37] border-[#D4AF37]/40",
  robberies: "text-[#E26B6B] border-[#E26B6B]/40",
};

/* ---------- Sub editors for each section type ---------- */

const BodyEditor = ({ s, onChange }) => (
  <Field label="Testo paragrafo">
    <TextArea value={s.body} onChange={(x) => onChange({ ...s, body: x })} rows={5} />
  </Field>
);

const ListEditor = ({ s, onChange }) => {
  const items = s.list || [];
  const setItems = (next) => onChange({ ...s, list: next });
  return (
    <div>
      <span className="block text-[10px] uppercase tracking-[0.26em] text-[#D4AF37] mb-2">
        Voci dell&apos;elenco
      </span>
      {items.map((it, i) => (
        <div key={i} className="flex gap-2 mb-2">
          <TextInput value={it} onChange={(x) => setItems(items.map((y, j) => (j === i ? x : y)))} />
          <button
            onClick={() => {
              if (i === 0) return;
              const arr = [...items];
              [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
              setItems(arr);
            }}
            className="p-2 rounded border border-white/10 text-[#B6B6BE]"
            aria-label="Su"
          >
            <ArrowUp size={12} />
          </button>
          <button
            onClick={() => {
              if (i === items.length - 1) return;
              const arr = [...items];
              [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
              setItems(arr);
            }}
            className="p-2 rounded border border-white/10 text-[#B6B6BE]"
            aria-label="Giù"
          >
            <ArrowDown size={12} />
          </button>
          <button
            onClick={() => setItems(items.filter((_, j) => j !== i))}
            className="p-2 rounded border border-[#E26B6B]/35 text-[#E26B6B]"
            aria-label="Elimina"
          >
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <button
        onClick={() => setItems([...items, "Nuova voce"])}
        className="mt-1 btn-ghost-neon inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em]"
      >
        <Plus size={12} /> Aggiungi voce
      </button>
    </div>
  );
};

const DefsEditor = ({ s, onChange }) => {
  const defs = s.defs || [];
  const setDefs = (next) => onChange({ ...s, defs: next });
  return (
    <div>
      <span className="block text-[10px] uppercase tracking-[0.26em] text-[#D4AF37] mb-2">
        Card definizioni (termine + descrizione)
      </span>
      {defs.map((d, i) => (
        <div key={i} className="mb-3 rounded-xl p-4 border border-white/8 bg-[#08080C]/60">
          <div className="flex justify-end gap-1 mb-2">
            <button
              onClick={() => {
                if (i === 0) return;
                const arr = [...defs];
                [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                setDefs(arr);
              }}
              className="p-1.5 rounded border border-white/10 text-[#B6B6BE]"
            >
              <ArrowUp size={11} />
            </button>
            <button
              onClick={() => {
                if (i === defs.length - 1) return;
                const arr = [...defs];
                [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
                setDefs(arr);
              }}
              className="p-1.5 rounded border border-white/10 text-[#B6B6BE]"
            >
              <ArrowDown size={11} />
            </button>
            <button
              onClick={() => setDefs(defs.filter((_, j) => j !== i))}
              className="p-1.5 rounded border border-[#E26B6B]/35 text-[#E26B6B]"
            >
              <Trash2 size={11} />
            </button>
          </div>
          <Field label="Termine">
            <TextInput value={d.t} onChange={(x) => setDefs(defs.map((y, j) => (j === i ? { ...y, t: x } : y)))} />
          </Field>
          <Field label="Definizione">
            <TextArea value={d.d} onChange={(x) => setDefs(defs.map((y, j) => (j === i ? { ...y, d: x } : y)))} rows={3} />
          </Field>
        </div>
      ))}
      <button
        onClick={() => setDefs([...defs, { t: "Nuovo termine", d: "Definizione…" }])}
        className="mt-1 btn-ghost-neon inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em]"
      >
        <Plus size={12} /> Aggiungi definizione
      </button>
    </div>
  );
};

const TableEditor = ({ s, onChange }) => {
  const table = s.table || { head: [], rows: [] };
  const setTable = (next) => onChange({ ...s, table: next });
  const cols = table.head.length;
  return (
    <div>
      <span className="block text-[10px] uppercase tracking-[0.26em] text-[#D4AF37] mb-2">
        Intestazioni colonne
      </span>
      <div className="flex flex-wrap gap-2 mb-3">
        {table.head.map((h, i) => (
          <div key={i} className="flex gap-1">
            <TextInput
              value={h}
              onChange={(x) => setTable({ ...table, head: table.head.map((y, j) => (j === i ? x : y)) })}
            />
            <button
              onClick={() => {
                const newHead = table.head.filter((_, j) => j !== i);
                const newRows = table.rows.map((r) => r.filter((_, j) => j !== i));
                setTable({ head: newHead, rows: newRows });
              }}
              className="p-2 rounded border border-[#E26B6B]/35 text-[#E26B6B] shrink-0"
              aria-label="Elimina colonna"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        <button
          onClick={() =>
            setTable({
              head: [...table.head, `Col ${cols + 1}`],
              rows: table.rows.map((r) => [...r, ""]),
            })
          }
          className="btn-ghost-neon inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[11px] uppercase tracking-[0.16em]"
        >
          <Plus size={11} /> Colonna
        </button>
      </div>

      <span className="block text-[10px] uppercase tracking-[0.26em] text-[#D4AF37] mb-2 mt-4">
        Righe
      </span>
      <div className="rounded-xl border border-white/8 bg-[#08080C]/40 p-3">
        {table.rows.map((row, ri) => (
          <div key={ri} className="flex gap-2 mb-2 items-start">
            <span className="font-mono-pl text-[10px] text-[#B6B6BE] pt-3 w-6 shrink-0">
              {String(ri + 1).padStart(2, "0")}
            </span>
            <div className="grid gap-1.5 flex-1" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
              {row.map((cell, ci) => (
                <TextInput
                  key={ci}
                  value={cell}
                  onChange={(x) =>
                    setTable({
                      ...table,
                      rows: table.rows.map((r, j) =>
                        j === ri ? r.map((c, k) => (k === ci ? x : c)) : r,
                      ),
                    })
                  }
                />
              ))}
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <button
                onClick={() => {
                  if (ri === 0) return;
                  const arr = [...table.rows];
                  [arr[ri - 1], arr[ri]] = [arr[ri], arr[ri - 1]];
                  setTable({ ...table, rows: arr });
                }}
                className="p-1.5 rounded border border-white/10 text-[#B6B6BE]"
              >
                <ArrowUp size={11} />
              </button>
              <button
                onClick={() => {
                  if (ri === table.rows.length - 1) return;
                  const arr = [...table.rows];
                  [arr[ri + 1], arr[ri]] = [arr[ri], arr[ri + 1]];
                  setTable({ ...table, rows: arr });
                }}
                className="p-1.5 rounded border border-white/10 text-[#B6B6BE]"
              >
                <ArrowDown size={11} />
              </button>
              <button
                onClick={() => setTable({ ...table, rows: table.rows.filter((_, j) => j !== ri) })}
                className="p-1.5 rounded border border-[#E26B6B]/35 text-[#E26B6B]"
              >
                <Trash2 size={11} />
              </button>
            </div>
          </div>
        ))}
        <button
          onClick={() => setTable({ ...table, rows: [...table.rows, Array(cols).fill("")] })}
          className="btn-ghost-neon inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[11px] uppercase tracking-[0.16em] mt-1"
        >
          <Plus size={11} /> Riga
        </button>
      </div>
    </div>
  );
};

const RobberiesEditor = ({ s, onChange }) => {
  const robs = s.robberies || [];
  const setRobs = (next) => onChange({ ...s, robberies: next });
  const updateRob = (i, patch) => setRobs(robs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div>
      <span className="block text-[10px] uppercase tracking-[0.26em] text-[#D4AF37] mb-2">
        Card rapine (stats numerici + regole specifiche)
      </span>
      {robs.map((r, i) => (
        <div key={i} className="mb-4 rounded-xl p-4 border border-[#E26B6B]/30 bg-[#08080C]/60">
          <div className="flex justify-between items-center mb-3">
            <p className="font-mono-pl text-[10px] tracking-[0.22em] text-[#E26B6B]">
              {String(i + 1).padStart(2, "0")} · {r.name}
            </p>
            <div className="flex gap-1">
              <button
                onClick={() => {
                  if (i === 0) return;
                  const arr = [...robs];
                  [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                  setRobs(arr);
                }}
                className="p-1.5 rounded border border-white/10 text-[#B6B6BE]"
              >
                <ArrowUp size={11} />
              </button>
              <button
                onClick={() => {
                  if (i === robs.length - 1) return;
                  const arr = [...robs];
                  [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
                  setRobs(arr);
                }}
                className="p-1.5 rounded border border-white/10 text-[#B6B6BE]"
              >
                <ArrowDown size={11} />
              </button>
              <button
                onClick={() => setRobs(robs.filter((_, j) => j !== i))}
                className="p-1.5 rounded border border-[#E26B6B]/35 text-[#E26B6B]"
              >
                <Trash2 size={11} />
              </button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome">
              <TextInput value={r.name} onChange={(x) => updateRob(i, { name: x })} />
            </Field>
            <Field label="Sottotitolo">
              <TextInput value={r.sub} onChange={(x) => updateRob(i, { sub: x })} />
            </Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Rapinatori">
              <TextInput
                value={r.stats?.rapinatori || ""}
                onChange={(x) => updateRob(i, { stats: { ...r.stats, rapinatori: x } })}
              />
            </Field>
            <Field label="Poliziotti">
              <TextInput
                value={r.stats?.poliziotti || ""}
                onChange={(x) => updateRob(i, { stats: { ...r.stats, poliziotti: x } })}
              />
            </Field>
            <Field label="Ostaggi">
              <TextInput
                value={r.stats?.ostaggi || ""}
                onChange={(x) => updateRob(i, { stats: { ...r.stats, ostaggi: x } })}
              />
            </Field>
          </div>
          <div>
            <span className="block text-[10px] uppercase tracking-[0.26em] text-[#D4AF37] mb-2">
              Regole specifiche
            </span>
            {(r.rules || []).map((rule, k) => (
              <div key={k} className="flex gap-2 mb-2">
                <TextInput
                  value={rule}
                  onChange={(x) => updateRob(i, { rules: r.rules.map((y, j) => (j === k ? x : y)) })}
                />
                <button
                  onClick={() => updateRob(i, { rules: r.rules.filter((_, j) => j !== k) })}
                  className="p-2 rounded border border-[#E26B6B]/35 text-[#E26B6B]"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            <button
              onClick={() => updateRob(i, { rules: [...(r.rules || []), "Nuova regola"] })}
              className="mt-1 btn-ghost-neon inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[11px] uppercase tracking-[0.16em]"
            >
              <Plus size={11} /> Regola
            </button>
          </div>
        </div>
      ))}
      <button
        onClick={() =>
          setRobs([
            ...robs,
            {
              name: "Nuova rapina",
              sub: "Descrizione breve",
              stats: { rapinatori: "1 — 4", poliziotti: "2 — 4", ostaggi: "—" },
              rules: ["Regola 1"],
            },
          ])
        }
        className="mt-1 btn-ghost-neon inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em]"
      >
        <Plus size={12} /> Aggiungi card rapina
      </button>
    </div>
  );
};

/* ---------- Single section card (collapsible) ---------- */
const SectionCard = ({ s, sIndex, total, onChange, onRemove, onMove }) => {
  const [open, setOpen] = useState(false);
  const type = sectionType(s);
  return (
    <div className="mb-3 rounded-xl border border-white/8 bg-[#08080C]/70">
      <div className="flex items-center gap-2 p-3">
        <button
          onClick={() => setOpen((o) => !o)}
          className="p-1.5 rounded text-[#B6B6BE] hover:text-[#F5F5F5]"
          aria-label={open ? "Comprimi" : "Espandi"}
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <span className={`font-mono-pl text-[9px] uppercase tracking-[0.22em] px-2 py-1 rounded border ${SECTION_BADGE_COLOR[type]}`}>
          {SECTION_LABELS[type]}
        </span>
        <span className="text-sm text-[#F5F5F5] font-medium truncate flex-1">
          {s.h || "(senza titolo)"}
        </span>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={() => onMove(sIndex, -1)}
            disabled={sIndex === 0}
            className="p-1.5 rounded border border-white/10 text-[#B6B6BE] disabled:opacity-30"
          >
            <ArrowUp size={11} />
          </button>
          <button
            onClick={() => onMove(sIndex, 1)}
            disabled={sIndex === total - 1}
            className="p-1.5 rounded border border-white/10 text-[#B6B6BE] disabled:opacity-30"
          >
            <ArrowDown size={11} />
          </button>
          <button
            onClick={onRemove}
            className="p-1.5 rounded border border-[#E26B6B]/35 text-[#E26B6B]"
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-white/[0.04]">
          <Field label="Titolo sezione (h)">
            <TextInput value={s.h} onChange={(x) => onChange({ ...s, h: x })} />
          </Field>
          {type === "body" && <BodyEditor s={s} onChange={onChange} />}
          {type === "list" && <ListEditor s={s} onChange={onChange} />}
          {type === "defs" && <DefsEditor s={s} onChange={onChange} />}
          {type === "table" && <TableEditor s={s} onChange={onChange} />}
          {type === "robberies" && <RobberiesEditor s={s} onChange={onChange} />}
        </div>
      )}
    </div>
  );
};

/* ---------- Single chapter card (collapsible) ---------- */
const ChapterCard = ({ c, cIndex, total, onChange, onRemove, onMove }) => {
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const Icon = ICON_MAP[c.icon] || BookOpen;

  const updateSection = (sIndex, next) =>
    onChange({ ...c, sections: c.sections.map((s, i) => (i === sIndex ? next : s)) });

  const removeSection = (sIndex) =>
    onChange({ ...c, sections: c.sections.filter((_, i) => i !== sIndex) });

  const moveSection = (sIndex, dir) => {
    const j = sIndex + dir;
    if (j < 0 || j >= c.sections.length) return;
    const arr = [...c.sections];
    [arr[sIndex], arr[j]] = [arr[j], arr[sIndex]];
    onChange({ ...c, sections: arr });
  };

  const addSection = (type) => {
    onChange({
      ...c,
      sections: [...c.sections, JSON.parse(JSON.stringify(SECTION_TEMPLATES[type]))],
    });
    setAddOpen(false);
  };

  return (
    <div className="mb-5 rounded-2xl border border-[#D4AF37]/20 bg-[#0B0B0F]/70 overflow-hidden">
      <div className="flex items-center gap-3 p-4 sm:p-5 bg-gradient-to-r from-[#1A1A22]/70 to-transparent">
        <button
          onClick={() => setOpen((o) => !o)}
          className="p-1.5 rounded text-[#B6B6BE] hover:text-[#F5F5F5]"
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <span className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/35">
          <Icon size={15} className="text-[#D4AF37]" strokeWidth={1.7} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-mono-pl text-[9px] uppercase tracking-[0.22em] text-[#D4AF37]">
            {c.subtitle}
          </p>
          <p className="font-display text-base sm:text-lg uppercase text-[#F5F5F5] tracking-wide truncate">
            {c.title}
          </p>
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={() => onMove(cIndex, -1)}
            disabled={cIndex === 0}
            className="p-2 rounded border border-white/10 text-[#B6B6BE] disabled:opacity-30"
          >
            <ArrowUp size={12} />
          </button>
          <button
            onClick={() => onMove(cIndex, 1)}
            disabled={cIndex === total - 1}
            className="p-2 rounded border border-white/10 text-[#B6B6BE] disabled:opacity-30"
          >
            <ArrowDown size={12} />
          </button>
          <button
            onClick={onRemove}
            className="p-2 rounded border border-[#E26B6B]/35 text-[#E26B6B]"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {open && (
        <div className="p-5 sm:p-6 border-t border-white/[0.04]">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Titolo capitolo">
              <TextInput value={c.title} onChange={(x) => onChange({ ...c, title: x })} />
            </Field>
            <Field label="Sottotitolo (es. Capitolo I)">
              <TextInput value={c.subtitle} onChange={(x) => onChange({ ...c, subtitle: x })} />
            </Field>
            <Field label="Chiave (identificatore url-safe)" hint="Usata come ancora di navigazione. Solo lettere minuscole, nessuno spazio.">
              <TextInput
                value={c.key}
                onChange={(x) => onChange({ ...c, key: x.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
              />
            </Field>
            <Field label="Icona">
              <select
                value={c.icon || "BookOpen"}
                onChange={(e) => onChange({ ...c, icon: e.target.value })}
                className="search-input w-full px-3 py-3 rounded-xl bg-[#0B0B0F]/90 border border-white/10 text-sm text-[#F5F5F5] focus:outline-none focus:border-[#D4AF37]/55"
              >
                {CHAPTER_ICONS.map((ic) => (
                  <option key={ic} value={ic}>
                    {ic}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Intro (paragrafo introduttivo)">
            <TextArea
              value={c.intro}
              onChange={(x) => onChange({ ...c, intro: x })}
              rows={3}
            />
          </Field>

          <div className="mt-6 pt-5 border-t border-white/[0.06]">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display text-sm uppercase tracking-wide text-[#F5F5F5]">
                Sezioni del capitolo ({c.sections.length})
              </h4>
              <div className="relative">
                <button
                  onClick={() => setAddOpen((v) => !v)}
                  data-testid={`add-section-${c.key}`}
                  className="btn-ghost-neon inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.16em]"
                >
                  <Plus size={12} /> Aggiungi sezione
                </button>
                {addOpen && (
                  <div
                    className="absolute right-0 top-full mt-2 z-20 rounded-xl border border-white/10 bg-[#08080C] shadow-2xl min-w-[220px] overflow-hidden"
                    style={{ boxShadow: "0 20px 60px -10px rgba(0,0,0,0.85)" }}
                  >
                    {Object.keys(SECTION_TEMPLATES).map((t) => (
                      <button
                        key={t}
                        onClick={() => addSection(t)}
                        className="w-full text-left px-4 py-2.5 text-xs text-[#B6B6BE] hover:bg-[#D4AF37]/10 hover:text-[#F5F5F5] flex items-center justify-between"
                      >
                        <span>{SECTION_LABELS[t]}</span>
                        <span className={`font-mono-pl text-[9px] tracking-[0.18em] px-1.5 py-0.5 rounded border ${SECTION_BADGE_COLOR[t]}`}>
                          {t}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {c.sections.length === 0 && (
              <p className="text-xs text-[#B6B6BE]/60 italic py-4">
                Nessuna sezione. Aggiungi paragrafi, liste, tabelle o card rapine.
              </p>
            )}

            {c.sections.map((s, i) => (
              <SectionCard
                key={i}
                s={s}
                sIndex={i}
                total={c.sections.length}
                onChange={(next) => updateSection(i, next)}
                onRemove={() => removeSection(i)}
                onMove={moveSection}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const RulesEditor = () => {
  const [v, setV] = useSectionState("rules");
  if (!v) return null;

  const updateChapter = (i, next) => setV(v.map((c, j) => (j === i ? next : c)));
  const removeChapter = (i) => setV(v.filter((_, j) => j !== i));
  const moveChapter = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= v.length) return;
    const arr = [...v];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setV(arr);
  };
  const addChapter = () =>
    setV([
      ...v,
      {
        key: `capitolo-${Date.now()}`,
        icon: "BookOpen",
        title: "Nuovo capitolo",
        subtitle: `Capitolo ${v.length + 1}`,
        intro: "Introduzione del capitolo…",
        sections: [],
      },
    ]);

  return (
    <EditorShell
      title="Regolamento — Editor avanzato"
      description="Gestisci capitoli, sezioni, tabelle e card rapine del Tomo Olografico. Riordina e modifica tutto in-place: niente codice, solo contenuto."
      section="rules"
      value={v}
      setValue={setV}
      validate={(arr) => {
        if (!Array.isArray(arr) || arr.length === 0) return "Aggiungi almeno un capitolo";
        for (const c of arr) {
          if (!c.key) return "Tutti i capitoli devono avere una chiave";
          if (!c.title) return "Tutti i capitoli devono avere un titolo";
        }
        const keys = arr.map((c) => c.key);
        if (new Set(keys).size !== keys.length) return "Le chiavi dei capitoli devono essere univoche";
        return null;
      }}
    >
      <div className="mb-5 rounded-xl px-4 py-3 border border-[#00D4FF]/25 bg-[#00D4FF]/5">
        <p className="text-xs text-[#B6B6BE] leading-relaxed">
          <span className="text-[#00D4FF] font-semibold uppercase tracking-[0.18em] text-[10px]">
            Tip
          </span>
          {" "}
          — Clicca su un capitolo per espanderlo. Ogni capitolo può contenere paragrafi, elenchi puntati, card di definizioni, tabelle e card rapine. Usa l&apos;anteprima live per vedere come appare nel libro 3D.
        </p>
      </div>

      {v.map((c, i) => (
        <ChapterCard
          key={c.key + i}
          c={c}
          cIndex={i}
          total={v.length}
          onChange={(next) => updateChapter(i, next)}
          onRemove={() => removeChapter(i)}
          onMove={moveChapter}
        />
      ))}

      <button
        onClick={addChapter}
        data-testid="add-chapter"
        className="mt-2 btn-ghost-neon inline-flex items-center gap-2 rounded-full px-5 py-3 text-xs uppercase tracking-[0.18em]"
      >
        <Plus size={13} /> Aggiungi capitolo
      </button>
    </EditorShell>
  );
};


// ============ UNIVERSE (Saga / Lore Cinematica) ============

const ArchPanelEditor = ({ label, value, onChange, accent = "#D4AF37" }) => {
  const tenets = Array.isArray(value.tenets) ? value.tenets : [];
  return (
    <div
      className="rounded-2xl p-5 border bg-[#08080C]/60"
      style={{ borderColor: `${accent}33` }}
    >
      <p
        className="font-mono-pl text-[10px] uppercase tracking-[0.28em] mb-4"
        style={{ color: accent }}
      >
        {label}
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Tag (badge piccolo)">
          <TextInput value={value.tag} onChange={(x) => onChange({ ...value, tag: x })} />
        </Field>
        <Field label="Sottotitolo (corsivo)">
          <TextInput value={value.subtitle} onChange={(x) => onChange({ ...value, subtitle: x })} />
        </Field>
      </div>
      <Field label="Titolo">
        <TextInput value={value.title} onChange={(x) => onChange({ ...value, title: x })} />
      </Field>
      <Field label="Corpo narrativo">
        <TextArea value={value.body} onChange={(x) => onChange({ ...value, body: x })} rows={6} />
      </Field>
      <span className="block text-[10px] uppercase tracking-[0.26em] text-[#B6B6BE] mb-2 mt-2">
        Pilastri / Vincoli (4 keyword)
      </span>
      {tenets.map((t, i) => (
        <div key={i} className="flex gap-2 mb-2">
          <TextInput
            value={t}
            onChange={(x) => onChange({ ...value, tenets: tenets.map((y, j) => (j === i ? x : y)) })}
          />
          <button
            onClick={() => onChange({ ...value, tenets: tenets.filter((_, j) => j !== i) })}
            className="p-2 rounded border border-[#E26B6B]/35 text-[#E26B6B]"
          >
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange({ ...value, tenets: [...tenets, "Nuovo"] })}
        className="mt-1 btn-ghost-neon inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em]"
      >
        <Plus size={12} /> Aggiungi
      </button>
    </div>
  );
};

const ActEditorCard = ({ act, idx, total, onChange, onRemove, onMove }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-4 rounded-2xl border border-[#D4AF37]/20 bg-[#0B0B0F]/70 overflow-hidden">
      <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-[#1A1A22]/70 to-transparent">
        <button
          onClick={() => setOpen((o) => !o)}
          className="p-1.5 rounded text-[#B6B6BE] hover:text-[#F5F5F5]"
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <span className="font-display text-3xl text-[#D4AF37] glow-gold w-14 text-center shrink-0">
          {act.n || String(idx + 1)}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-mono-pl text-[9px] uppercase tracking-[0.22em] text-[#D4AF37]">
            {act.code || `Atto ${idx + 1}`} · {act.timeframe}
          </p>
          <p className="font-display text-base uppercase text-[#F5F5F5] tracking-wide truncate">
            {act.title || "(senza titolo)"}
          </p>
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={() => onMove(idx, -1)}
            disabled={idx === 0}
            className="p-2 rounded border border-white/10 text-[#B6B6BE] disabled:opacity-30"
          >
            <ArrowUp size={12} />
          </button>
          <button
            onClick={() => onMove(idx, 1)}
            disabled={idx === total - 1}
            className="p-2 rounded border border-white/10 text-[#B6B6BE] disabled:opacity-30"
          >
            <ArrowDown size={12} />
          </button>
          <button
            onClick={onRemove}
            className="p-2 rounded border border-[#E26B6B]/35 text-[#E26B6B]"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {open && (
        <div className="p-5 sm:p-6 border-t border-white/[0.04]">
          <div className="grid sm:grid-cols-4 gap-3">
            <Field label="Numero (I, II…)">
              <TextInput value={act.n} onChange={(x) => onChange({ ...act, n: x.toUpperCase() })} />
            </Field>
            <Field label="Code (es. Atto Primo)">
              <TextInput value={act.code} onChange={(x) => onChange({ ...act, code: x })} />
            </Field>
            <Field label="Periodo (es. Mese 1)">
              <TextInput
                value={act.timeframe}
                onChange={(x) => onChange({ ...act, timeframe: x })}
              />
            </Field>
            <Field label="Titolo">
              <TextInput value={act.title} onChange={(x) => onChange({ ...act, title: x })} />
            </Field>
          </div>
          <Field label="Tagline (frase d'effetto)">
            <TextInput
              value={act.tagline}
              onChange={(x) => onChange({ ...act, tagline: x })}
              placeholder="es. Il principio del caos"
            />
          </Field>
          <Field label="Narrazione">
            <TextArea
              value={act.narrative}
              onChange={(x) => onChange({ ...act, narrative: x })}
              rows={6}
            />
          </Field>
        </div>
      )}
    </div>
  );
};

export const UniverseEditor = () => {
  const [v, setV] = useSectionState("universe");
  if (!v) return null;

  const acts = Array.isArray(v.acts) ? v.acts : [];
  const rumors = Array.isArray(v.rumors) ? v.rumors : [];

  const updateAct = (i, next) =>
    setV({ ...v, acts: acts.map((a, j) => (j === i ? next : a)) });
  const removeAct = (i) => setV({ ...v, acts: acts.filter((_, j) => j !== i) });
  const moveAct = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= acts.length) return;
    const arr = [...acts];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setV({ ...v, acts: arr });
  };
  const addAct = () => {
    const romans = ["I", "II", "III", "IV", "V", "VI", "VII"];
    const next = acts.length + 1;
    setV({
      ...v,
      acts: [
        ...acts,
        {
          n: romans[next - 1] || String(next),
          code: `Atto ${romans[next - 1] || next}`,
          title: "Nuovo atto",
          timeframe: `Mese ${next}`,
          tagline: "Tagline cinematografica",
          narrative: "Narrazione del nuovo atto…",
        },
      ],
    });
  };

  return (
    <EditorShell
      title="L'Universo — Saga & Atti"
      description="La nuova sezione cinematografica del sito: hero, architettura del potere (Sogno di Vetro vs Abisso delle Ombre), i 4 Atti del ciclo della città, voci di strada misteriose, CTA finale. Tutto editabile senza svelare le meccaniche staff."
      section="universe"
      value={v}
      setValue={setV}
    >
      {/* HERO */}
      <div className="mb-8 pb-6 border-b border-white/[0.06]">
        <p className="font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#00D4FF] mb-3">
          Hero della saga
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Kicker">
            <TextInput
              value={v.hero?.kicker}
              onChange={(x) => setV({ ...v, hero: { ...v.hero, kicker: x } })}
            />
          </Field>
          <Field label="Intro (sottotitolo poetico)">
            <TextInput
              value={v.hero?.intro}
              onChange={(x) => setV({ ...v, hero: { ...v.hero, intro: x } })}
            />
          </Field>
          <Field label="Titolo — prima parte">
            <TextInput
              value={v.hero?.title_lead}
              onChange={(x) => setV({ ...v, hero: { ...v.hero, title_lead: x } })}
            />
          </Field>
          <Field label="Titolo — parte accent (corsivo oro)">
            <TextInput
              value={v.hero?.title_accent}
              onChange={(x) => setV({ ...v, hero: { ...v.hero, title_accent: x } })}
            />
          </Field>
        </div>
      </div>

      {/* ARCHITECTURE */}
      <div className="mb-8 pb-6 border-b border-white/[0.06]">
        <p className="font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3">
          Architettura del Potere — Split screen
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Kicker">
            <TextInput
              value={v.architecture?.kicker}
              onChange={(x) =>
                setV({ ...v, architecture: { ...v.architecture, kicker: x } })
              }
            />
          </Field>
          <Field label="Titolo sezione">
            <TextInput
              value={v.architecture?.title}
              onChange={(x) =>
                setV({ ...v, architecture: { ...v.architecture, title: x } })
              }
            />
          </Field>
        </div>
        <Field label="Intro architettura">
          <TextArea
            value={v.architecture?.intro}
            onChange={(x) =>
              setV({ ...v, architecture: { ...v.architecture, intro: x } })
            }
            rows={2}
          />
        </Field>
        <div className="grid lg:grid-cols-2 gap-4 mt-4">
          <ArchPanelEditor
            label="Pannello LUCE — Il Sogno di Vetro"
            value={v.architecture?.light || {}}
            onChange={(next) =>
              setV({ ...v, architecture: { ...v.architecture, light: next } })
            }
            accent="#D4AF37"
          />
          <ArchPanelEditor
            label="Pannello OMBRA — L'Abisso"
            value={v.architecture?.shadow || {}}
            onChange={(next) =>
              setV({ ...v, architecture: { ...v.architecture, shadow: next } })
            }
            accent="#E26B6B"
          />
        </div>
      </div>

      {/* ACTS — Hidden from public, kept in admin only as a read-only notice */}
      <div className="mb-8 pb-6 border-b border-white/[0.06]">
        <div className="rounded-xl px-4 py-3 border border-[#E26B6B]/30 bg-[#E26B6B]/5 flex items-start gap-2">
          <Lock size={14} className="text-[#E26B6B] mt-0.5 shrink-0" />
          <div>
            <p className="font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#E26B6B]">
              I Quattro Atti — Riservato Staff
            </p>
            <p className="text-xs text-[#B6B6BE] mt-1 leading-relaxed">
              Questa sezione non è più mostrata sul sito pubblico. Il contenuto del ciclo è ora nel
              {" "}
              <a href="/admin/bible" className="text-[#E26B6B] underline">vault della Bibbia</a>
              {" "}
              → capitolo «Il Ciclo dei 6 Mesi». I dati restano in DB per backward-compat ma non vengono renderizzati.
            </p>
          </div>
        </div>
      </div>

      {/* RUMORS */}
      <div className="mb-8 pb-6 border-b border-white/[0.06]">
        <p className="font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#E26B6B] mb-3">
          Voci della Strada — carousel misterioso
        </p>
        <p className="text-xs text-[#B6B6BE]/70 mb-3">
          Le frasi ruotano ogni 4 secondi sul sito. Mantieni il tono ambiguo: niente meccaniche staff esplicite.
        </p>
        {rumors.map((r, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <TextInput
              value={r}
              onChange={(x) =>
                setV({ ...v, rumors: rumors.map((y, j) => (j === i ? x : y)) })
              }
            />
            <button
              onClick={() => {
                if (i === 0) return;
                const arr = [...rumors];
                [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                setV({ ...v, rumors: arr });
              }}
              className="p-2 rounded border border-white/10 text-[#B6B6BE]"
            >
              <ArrowUp size={12} />
            </button>
            <button
              onClick={() => {
                if (i === rumors.length - 1) return;
                const arr = [...rumors];
                [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
                setV({ ...v, rumors: arr });
              }}
              className="p-2 rounded border border-white/10 text-[#B6B6BE]"
            >
              <ArrowDown size={12} />
            </button>
            <button
              onClick={() => setV({ ...v, rumors: rumors.filter((_, j) => j !== i) })}
              className="p-2 rounded border border-[#E26B6B]/35 text-[#E26B6B]"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        <button
          onClick={() => setV({ ...v, rumors: [...rumors, "Nuova voce di strada…"] })}
          data-testid="add-universe-rumor"
          className="mt-1 btn-ghost-neon inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em]"
        >
          <Plus size={12} /> Aggiungi voce
        </button>
      </div>

      {/* OUTRO */}
      <div>
        <p className="font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#A78BFA] mb-3">
          Outro & CTA finale
        </p>
        <Field label="Kicker outro">
          <TextInput
            value={v.outro?.kicker}
            onChange={(x) => setV({ ...v, outro: { ...v.outro, kicker: x } })}
          />
        </Field>
        <Field label="Titolo">
          <TextInput
            value={v.outro?.title}
            onChange={(x) => setV({ ...v, outro: { ...v.outro, title: x } })}
          />
        </Field>
        <Field label="Body">
          <TextArea
            value={v.outro?.body}
            onChange={(x) => setV({ ...v, outro: { ...v.outro, body: x } })}
            rows={3}
          />
        </Field>
        <Field label="Etichetta bottone CTA">
          <TextInput
            value={v.outro?.cta_label}
            onChange={(x) => setV({ ...v, outro: { ...v.outro, cta_label: x } })}
          />
        </Field>
      </div>
    </EditorShell>
  );
};


// ============ RULES META (dedicated /regolamento page) ============
export const RulesMetaEditor = () => {
  const [v, setV] = useSectionState("rules_meta");
  if (!v) return null;
  const hero = v.hero || {};
  const toolbar = v.toolbar || {};
  const footer = v.footer || {};
  const staffNote = v.staff_note || {};
  const patch = (k, val) => setV({ ...v, [k]: { ...v[k], ...val } });

  return (
    <EditorShell
      title="Regolamento — Pagina dedicata"
      description="Personalizza hero, toolbar, nota staff e footer della pagina /regolamento. I 12 capitoli e le sezioni si gestiscono da 'Regolamento (capitoli)'."
      section="rules_meta"
      value={v}
      setValue={setV}
    >
      <div className="mb-6">
        <a
          href="/regolamento"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.22em] border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B0B0F] transition-all"
          data-testid="rules-meta-open-preview"
        >
          <BookOpen size={13} /> Apri anteprima pagina
        </a>
      </div>

      <div className="mb-8 rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.03] p-5">
        <div className="font-mono-pl text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">
          Hero · Testata pagina
        </div>
        <Field label="Kicker (piccolo testo sopra il titolo)">
          <TextInput value={hero.kicker} onChange={(x) => patch("hero", { kicker: x })} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Titolo — parte principale">
            <TextInput value={hero.title_lead} onChange={(x) => patch("hero", { title_lead: x })} />
          </Field>
          <Field label="Titolo — accento oro">
            <TextInput value={hero.title_accent} onChange={(x) => patch("hero", { title_accent: x })} />
          </Field>
        </div>
        <Field label="Sottotitolo / Intro">
          <TextArea value={hero.subtitle} onChange={(x) => patch("hero", { subtitle: x })} rows={3} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Badge (pillola oro in alto a destra)">
            <TextInput value={hero.badge} onChange={(x) => patch("hero", { badge: x })} />
          </Field>
          <Field label="Immagine di sfondo (URL, opzionale)" hint="Se vuoto viene mostrato solo il glow oro cinematografico.">
            <TextInput value={hero.image_url} onChange={(x) => patch("hero", { image_url: x })} placeholder="https://..." />
          </Field>
        </div>
      </div>

      <div className="mb-8 rounded-2xl border border-white/10 p-5">
        <div className="font-mono-pl text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">
          Toolbar · Etichette
        </div>
        <Field label="Placeholder barra di ricerca">
          <TextInput value={toolbar.search_placeholder} onChange={(x) => patch("toolbar", { search_placeholder: x })} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Etichetta bottone 'Stampa'">
            <TextInput value={toolbar.print_label} onChange={(x) => patch("toolbar", { print_label: x })} />
          </Field>
          <Field label="Aria-label bottone 'Torna in cima'">
            <TextInput value={toolbar.back_top_label} onChange={(x) => patch("toolbar", { back_top_label: x })} />
          </Field>
        </div>
      </div>

      <div className="mb-8 rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/[0.06] to-transparent p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono-pl text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">
            Nota dello Staff (opzionale, in cima)
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!staffNote.enabled}
              onChange={(e) => patch("staff_note", { enabled: e.target.checked })}
              className="accent-[#D4AF37]"
              data-testid="rules-meta-staff-note-toggle"
            />
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#B6B6BE]">
              {staffNote.enabled ? "Attiva" : "Disattivata"}
            </span>
          </label>
        </div>
        <Field label="Titolo nota">
          <TextInput value={staffNote.title} onChange={(x) => patch("staff_note", { title: x })} />
        </Field>
        <Field label="Testo nota (visibile solo se attiva)" hint="Perfetta per annunci lampo: nuovi aggiornamenti, hotfix regolamento, avvisi urgenti.">
          <TextArea value={staffNote.body} onChange={(x) => patch("staff_note", { body: x })} rows={4} />
        </Field>
      </div>

      <div className="rounded-2xl border border-white/10 p-5">
        <div className="font-mono-pl text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">
          Footer · Chiusura pagina
        </div>
        <Field label="Citazione">
          <TextInput value={footer.quote} onChange={(x) => patch("footer", { quote: x })} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Firma">
            <TextInput value={footer.signature} onChange={(x) => patch("footer", { signature: x })} />
          </Field>
          <Field label="Etichetta bottone CTA">
            <TextInput value={footer.cta_label} onChange={(x) => patch("footer", { cta_label: x })} />
          </Field>
        </div>
      </div>
    </EditorShell>
  );
};
